<template>
  <div class="sleep-strip">
    <div ref="track" class="bars" aria-label="Sleep timeline (randomized, micro-bars)">
      <!-- micro bars (thin + slight spacing) -->
      <div
        v-for="(b, i) in microBars"
        :key="i"
        class="bar"
        :class="b.kind"
        :style="barStyle(b)"
        aria-hidden="true"
      ></div>

      <!-- only 2 events -->
      <div
        v-for="(x, i) in eventPx"
        :key="'e-' + i"
        class="bar event"
        :style="eventStyle(x)"
        aria-hidden="true"
      ></div>
    </div>

    <div class="time">
      <span>{{ startTime }}</span>
      <span>{{ endTime }}</span>
    </div>

    <!-- Optional: regenerate button -->
    <!-- <button class="regen" @click="regenerate">Regenerate</button> -->
  </div>
</template>

<script>
export default {
  name: "SleepTimelineRandomMicroBars",
  data() {
    return {
      startTime: "11:30 PM",
      endTime: "08:15 AM",

      // micro-bar rendering (thin with slight spacing)
      barW: 2, // px
      barGap: 1, // px

      trackW: 0,

      // number of randomized segments (higher = more variation)
      segmentCount: 14,

      // ONLY 2 events (percent positions across full 0–100 timeline)
      eventsPct: [38, 81],

      // ✅ generated once, stored here (no computed side effects)
      segments: [],
    };
  },

  computed: {
    microBars() {
      if (!this.trackW || !this.segments.length) return [];

      const step = this.barW + this.barGap;
      const totalBars = Math.floor(this.trackW / step);
      const bars = [];

      for (let i = 0; i < totalBars; i++) {
        const leftPx = i * step;
        const pct = (leftPx / this.trackW) * 100;
        const seg = this.findSegment(pct);

        bars.push({ kind: seg.kind, leftPx });
      }

      return bars;
    },

    eventPx() {
      if (!this.trackW) return [];
      return this.eventsPct.map((p) => (p / 100) * this.trackW);
    },
  },

  created() {
    this.generateSegments();
  },

  mounted() {
    this.measure();
    window.addEventListener("resize", this.measure, { passive: true });
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.measure);
  },

  methods: {
    measure() {
      this.$nextTick(() => {
        this.trackW = this.$refs.track ? this.$refs.track.clientWidth : 0;
      });
    },

    regenerate() {
      this.generateSegments();
      this.measure();
    },

    generateSegments() {
      // 1) random weights
      const raw = [];
      let totalWeight = 0;

      for (let i = 0; i < this.segmentCount; i++) {
        const kind = this.pickStage();
        const weight =
          kind === "awake"
            ? this.rand(2, 6)
            : kind === "rem"
            ? this.rand(5, 12)
            : this.rand(8, 18);

        raw.push({ kind, weight });
        totalWeight += weight;
      }

      // 2) normalize to 100%
      const widths = [];
      let acc = 0;

      for (let i = 0; i < raw.length; i++) {
        let pct;
        if (i === raw.length - 1) {
          pct = +(100 - acc).toFixed(2);
        } else {
          pct = +((raw[i].weight / totalWeight) * 100).toFixed(2);
          acc = +(acc + pct).toFixed(2);
        }
        widths.push(pct);
      }

      // 3) build segments with startPct
      const segs = [];
      let cursor = 0;

      for (let i = 0; i < raw.length; i++) {
        segs.push({
          kind: raw[i].kind,
          startPct: +cursor.toFixed(2),
          widthPct: widths[i],
        });
        cursor += widths[i];
      }

      // 4) tiny drift correction
      const sum = segs.reduce((s, x) => s + x.widthPct, 0);
      const drift = +(100 - sum).toFixed(2);
      if (Math.abs(drift) > 0.0001) {
        segs[segs.length - 1].widthPct = +(segs[segs.length - 1].widthPct + drift).toFixed(2);
      }

      // 5) recompute startPct precisely after drift
      let c = 0;
      for (let i = 0; i < segs.length; i++) {
        segs[i].startPct = +c.toFixed(2);
        c += segs[i].widthPct;
      }

      this.segments = segs;
    },

    pickStage() {
      const r = Math.random();
      if (r < 0.25) return "awake";
      if (r < 0.55) return "rem";
      return "deep";
    },

    rand(min, max) {
      return Math.random() * (max - min) + min;
    },

    findSegment(pct) {
      // pct in [0, 100)
      const seg =
        this.segments.find((s) => pct >= s.startPct && pct < s.startPct + s.widthPct) ||
        this.segments[this.segments.length - 1];

      return seg || { kind: "deep" };
    },

    stageGeometry(kind) {
      if (kind === "awake") return { top: "10%", height: "38%" };
      if (kind === "rem") return { top: "46%", height: "30%" };
      return { top: "74%", height: "16%" };
    },

    barStyle(b) {
      const geo = this.stageGeometry(b.kind);
      return {
        left: b.leftPx + "px",
        width: this.barW + "px",
        top: geo.top,
        height: geo.height,
      };
    },

    eventStyle(px) {
      return {
        left: px + "px",
        width: "2px",
        top: "8%",
        height: "84%",
      };
    },
  },
};
</script>

<style scoped>
.sleep-strip {
    width: 97%;
    margin-left: 1rem;
    margin-top: 1rem;
    background: #000;
    padding: 0.75rem;
    box-sizing: border-box;
}

.bars {
  position: relative;
  height: 160px;
  overflow: hidden;
  border-radius: 8px;
  background: radial-gradient(700px 120px at 50% 20%, rgba(255, 255, 255, 0.05), transparent 55%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent);
}

.bar {
  position: absolute;
  border-radius: 1px;
  opacity: 0.97;
}

/* stage colors */
.bar.awake {
  background: rgba(215, 225, 230, 0.95);
}
.bar.rem {
  background: rgba(120, 205, 198, 0.95);
}
.bar.deep {
  background: rgba(12, 70, 82, 0.95);
}

/* events */
.bar.event {
  background: rgba(255, 191, 0, 1);
  box-shadow: 0 0 0 1px rgba(255, 191, 0, 0.35);
  z-index: 3;
}

/* time labels */
.time {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  font-size: 18px;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.9);
}

/* Optional button */
/* .regen {
  margin-top: 12px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  color: #fff;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
} */
</style>
